// The Variables window's rows (spec 6.10.5): built in the browser from the open document (design scope) or
// from `GET /deployments/{id}/settings` (deployment scope), their statuses (6.10.7) and value cells (6.10.6).
// Nothing here branches on a provider or a concrete location type: only on capabilities, kind, secret and mode.
import type {
  AppRestartInfo,
  AppSettingsDrift,
  AppVariables,
  BlueprintConnection,
  BlueprintDocument,
  BlueprintResource,
  DeploymentVariables,
  DesignSide,
  ResourceSetting,
  ResourceType,
  SecretDriftKind,
  SecretMetadata,
  SettingNetworkRequest,
  SetValueCapability,
  SettingCloudDrift,
  SettingExternalTrigger,
  SettingDelivery,
  SettingKind,
  SettingLocation,
  SettingOrigin,
  SettingReferenceStatus,
  SettingSource,
  SyncState,
  TriggerSourceInfo,
} from '../api/types';
import type { Tone } from '../components/presentation';
import { connectionEnvironment, effectiveAlias, wiringRule } from '../lib/blueprint';
import { connectionKey, type DiagnosticIndex } from '../lib/diagnostics';
import { externalTriggerKind, externalTriggerOf } from '../lib/externalTriggers';
import { formatClock, formatDay, formatRelative } from '../lib/format';
import { isPlainObject } from '../lib/json';
import { secretReferences } from '../lib/secrets';
import { injectionMapName } from '../lib/settings';
import { strings } from '../strings';
import { deliveryEntry, locationEntry } from './registry';

const s = strings.variables;

/** One row of the Variables view, in either scope. */
export interface VariableRow {
  /** `<resourceId>/<name>`. */
  id: string;
  resourceId: string;
  resourceType: string;
  /** The row's key within the app: map key, JSON Pointer of a secret property, `<ALIAS>_<KEY>`, setting name. */
  name: string;
  /** What the app reads: the delivered name of a secret property when known, else `name`. */
  displayName: string;
  /** Secret properties: the JSON Pointer (`/registry/password`). */
  property: string | null;
  origin: SettingOrigin;
  source: SettingSource;
  location: SettingLocation;
  /** Null in design scope. */
  delivery: SettingDelivery | null;
  kind: SettingKind | null;
  secret: boolean;
  secretName: string | null;
  /** The raw value as written in the blueprint (null for literals and connection, trigger, platform rows). */
  expression: string | null;
  /** The literal or substituted value (non-secret). */
  value: string | null;
  resolvedValue: string | null;
  sync: SyncState | null;
  design: DesignSide | null;
  referenceStatus: SettingReferenceStatus | null;
  drift: SecretDriftKind[];
  pickupPendingUntil: string | null;
  /** Changed or removed in the cloud since the apply (a refresh found it). */
  cloudDrift: SettingCloudDrift | null;
  /** Feature spec 16 (3d): the external trigger that reads the setting (the first listing entry). */
  externalTrigger: SettingExternalTrigger | null;
  /** Feature spec 15: the per-app app read its references before the secret's current version was written. */
  olderVersionInUse?: boolean;
  metadata: SecretMetadata | null;
  scope: 'design' | 'deployment';
  /** The open document differs from its saved version for this variable (designer). */
  unsaved: boolean;
  /** The variable is an entry of the open document's injection map (design edits possible). */
  editable: boolean;
  /** Connection and trigger rows: the connection's key. */
  connectionKey: string | null;
  /** Design rows: messages of diagnostics that point at the variable. */
  problems: string[];
  /** Trigger placeholders in design scope (names are provider-defined). */
  placeholder: boolean;
  /** Feature spec 17, phase 2: a network request's `<ALIAS>_HOST` or `_PORT` (location `network-request`). */
  request?: SettingNetworkRequest | null;
}

/** An app of the scope: its rows and what the window shows about it. */
export interface VariableApp {
  resourceId: string;
  type: string;
  applied: boolean;
  referenceStatusCheckedAt: string | null;
  referenceStatusError: string | null;
  /** "Changed in the cloud" of the newest refresh after the last apply; null when none checked the app. */
  settingsDrift: AppSettingsDrift | null;
  /** Feature spec 15: the app's platform, its own reference status (per-app platforms) and its last rollout. */
  platform?: string | null;
  appReferenceStatus?: SettingReferenceStatus | null;
  lastRestart?: AppRestartInfo | null;
}

export const SETTING_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const EXACT_SECRET = /^\$\{\s*secret\.([A-Za-z0-9][A-Za-z0-9-]{0,62})\s*\}$/;
const EXACT_PARAMETER = /^\$\{\s*param\.([A-Za-z][A-Za-z0-9_]*)\s*\}$/;
const EXACT_OUTPUT = /^\$\{\s*resource\.([a-z][a-z0-9-]{0,29})\.([A-Za-z][A-Za-z0-9]*)\s*\}$/;

/** A location built in the browser for design rows (the server builds them in deployment scope). */
export function clientLocation(
  type: string,
  options: {
    container?: string | null;
    name?: string | null;
    secret?: boolean;
    setValue: SetValueCapability;
  },
): SettingLocation {
  const label = locationEntry({ type, secret: !!options.secret }).label({
    label: type,
    container: options.container ?? null,
  });
  return {
    type,
    label,
    container: options.container ?? null,
    name: options.name ?? null,
    nativeId: null,
    account: null,
    region: null,
    versionSelector: null,
    consoleUrl: null,
    secret: !!options.secret,
    capabilities: {
      reveal: false,
      setValue: options.setValue,
      drift: false,
      referenceStatus: false,
      versions: false,
      recover: false,
    },
    limits: null,
    softDelete: null,
    rotation: null,
  };
}

/** The design source of a value of an injection map (spec 6.10.5). */
export function classifyValue(value: string): {
  type: 'secret' | 'parameter' | 'output' | 'expression' | 'plain';
  secretName?: string;
  parameter?: string;
  output?: { resourceId: string; output: string };
} {
  const secret = EXACT_SECRET.exec(value);
  if (secret) return { type: 'secret', secretName: secret[1] };
  const parameter = EXACT_PARAMETER.exec(value);
  if (parameter) return { type: 'parameter', parameter: parameter[1] };
  const output = EXACT_OUTPUT.exec(value);
  if (output) return { type: 'output', output: { resourceId: output[1], output: output[2] } };
  return { type: value.includes('${') ? 'expression' : 'plain' };
}

/** Whether a resource type has variables: an injection map that is a top-level schema property. */
export function isApp(type: ResourceType | undefined): boolean {
  const name = injectionMapName(type);
  const properties = type?.propertiesSchema.properties;
  return !!name && isPlainObject(properties) && name in properties;
}

/** The entries of a resource's injection map as strings. */
export function injectionMap(
  resource: BlueprintResource,
  type: ResourceType | undefined,
): [string, string][] {
  const name = injectionMapName(type);
  const value = name ? resource.properties?.[name] : undefined;
  if (!isPlainObject(value)) return [];
  return Object.entries(value as Record<string, unknown>).map(([key, item]) => [
    key,
    typeof item === 'string' ? item : JSON.stringify(item),
  ]);
}

function baseRow(
  resource: BlueprintResource,
  name: string,
): Omit<VariableRow, 'location' | 'origin' | 'source'> {
  return {
    id: `${resource.id}/${name}`,
    resourceId: resource.id,
    resourceType: resource.type,
    name,
    displayName: name,
    property: null,
    delivery: null,
    kind: null,
    secret: false,
    secretName: null,
    expression: null,
    value: null,
    resolvedValue: null,
    sync: null,
    design: null,
    referenceStatus: null,
    drift: [],
    pickupPendingUntil: null,
    cloudDrift: null,
    externalTrigger: null,
    metadata: null,
    scope: 'design',
    unsaved: false,
    editable: false,
    connectionKey: null,
    problems: [],
    placeholder: false,
  };
}

/** The trigger description of a `triggers` connection (provider resolution, feature spec 16 9.3), or null. */
export type TriggerDescriber = (connection: BlueprintConnection) => TriggerSourceInfo | null;

/** Design-scope rows of one app, in document order (spec 6.10.5). */
export function designAppRows(
  resource: BlueprintResource,
  doc: BlueprintDocument,
  types: ReadonlyMap<string, ResourceType>,
  diagnostics?: DiagnosticIndex,
  describeTrigger?: TriggerDescriber,
): VariableRow[] {
  const type = types.get(resource.type);
  const mapName = injectionMapName(type);
  const rows: VariableRow[] = [];
  const problemsOf = (key: string) =>
    (diagnostics?.byResource.get(resource.id) ?? [])
      .filter(
        (d) =>
          d.target.kind === 'resource' &&
          d.target.area === 'properties' &&
          d.target.field?.[0] === mapName &&
          d.target.field[1] === key,
      )
      .map((d) => d.diagnostic.message);
  for (const [name, value] of injectionMap(resource, type)) {
    const kind = classifyValue(value);
    const row: VariableRow = {
      ...baseRow(resource, name),
      origin: 'settings',
      source: kind.type === 'secret' ? 'secret' : 'plain',
      location:
        kind.type === 'secret'
          ? clientLocation('secret', { name: kind.secretName, secret: true, setValue: 'none' })
          : kind.type === 'parameter'
            ? clientLocation('parameter', { name: kind.parameter, setValue: 'deployment' })
            : kind.type === 'output'
              ? clientLocation('output', {
                  container: kind.output!.resourceId,
                  name: `${kind.output!.resourceId}.${kind.output!.output}`,
                  setValue: 'none',
                })
              : kind.type === 'expression'
                ? clientLocation('expression', { name: value, setValue: 'design' })
                : clientLocation('plain', { setValue: 'design' }),
      secret: kind.type === 'secret',
      secretName: kind.secretName ?? null,
      kind: kind.type === 'secret' ? 'declared' : null,
      expression: kind.type === 'plain' ? null : value,
      value: kind.type === 'secret' ? null : value,
      resolvedValue: kind.type === 'plain' ? value : null,
      editable: true,
      problems: problemsOf(name),
      externalTrigger: externalTriggerOf(resource, type, name),
    };
    rows.push(row);
  }
  for (const [secretName, sites] of secretReferences({ ...doc, resources: [resource] })) {
    for (const site of sites) {
      if (mapName && site.path[0] === mapName) continue;
      const pointer = `/${site.path.join('/')}`;
      rows.push({
        ...baseRow(resource, pointer),
        property: pointer,
        origin: 'property',
        source: 'secret',
        location: clientLocation('secret', { name: secretName, secret: true, setValue: 'none' }),
        secret: true,
        secretName,
        kind: 'declared',
        expression: '${secret.' + secretName + '}',
      });
    }
  }
  for (const connection of doc.connections ?? []) {
    if (connection.type === 'connects-to' && connection.sourceId === resource.id) {
      const target = doc.resources.find((r) => r.id === connection.targetId);
      const rule = wiringRule(types, resource.type, target?.type);
      for (const variable of connectionEnvironment(connection, rule)) {
        rows.push({
          ...baseRow(resource, variable.name),
          origin: 'connection',
          source: variable.secret ? 'system-secret' : 'connection',
          location: clientLocation('connection', {
            container: connection.targetId,
            name: `${connection.targetId}.${variable.fromOutput}`,
            secret: variable.secret,
            setValue: 'none',
          }),
          secret: variable.secret,
          kind: variable.secret ? 'system' : null,
          connectionKey: connectionKey(connection),
        });
      }
    } else if (connection.type === 'triggers' && connection.targetId === resource.id) {
      const alias = effectiveAlias(connection) ?? connection.sourceId;
      // One row per setting of the provider's trigger description (feature spec 16, 9.7), else a placeholder.
      const description = describeTrigger?.(connection);
      if (description) {
        for (const setting of description.settings) {
          rows.push({
            ...baseRow(resource, `${alias}${setting.suffix}`),
            origin: 'trigger',
            source: 'trigger',
            location: clientLocation('trigger', {
              container: connection.sourceId,
              name: setting.suffix.replace(/^_+/, ''),
              setValue: 'none',
            }),
            connectionKey: connectionKey(connection),
          });
        }
        continue;
      }
      rows.push({
        ...baseRow(resource, `${alias}…`),
        origin: 'trigger',
        source: 'trigger',
        location: clientLocation('trigger', {
          container: connection.sourceId,
          name: s.table.providerDefined,
          setValue: 'none',
        }),
        connectionKey: connectionKey(connection),
        placeholder: true,
      });
    }
  }
  // Feature spec 17, phase 2 (6.15.24): a network request gives the app `<ALIAS>_HOST` and `<ALIAS>_PORT`, resolved per
  // deployment.
  for (const request of doc.networkRequests ?? []) {
    if (request.sourceId !== resource.id) continue;
    const alias = (request.alias || request.id).toUpperCase().replace(/-/g, '_');
    for (const suffix of ['_HOST', '_PORT'])
      rows.push({
        ...baseRow(resource, `${alias}${suffix}`),
        origin: 'network-request',
        source: 'plain',
        location: clientLocation('network-request', {
          container: request.id,
          name: request.id,
          setValue: 'none',
        }),
        request: {
          requestId: request.id,
          networkId: null,
          networkName: null,
          target: null,
          state: '',
          issue: null,
        },
      });
  }
  return rows;
}

/** Design-scope rows of every app of the document, by app. */
export function designRows(
  doc: BlueprintDocument,
  types: ReadonlyMap<string, ResourceType>,
  diagnostics?: DiagnosticIndex,
  describeTrigger?: TriggerDescriber,
): { apps: VariableApp[]; rows: VariableRow[] } {
  const apps: VariableApp[] = [];
  const rows: VariableRow[] = [];
  for (const resource of doc.resources) {
    if (!isApp(types.get(resource.type))) continue;
    apps.push({
      resourceId: resource.id,
      type: resource.type,
      applied: false,
      referenceStatusCheckedAt: null,
      referenceStatusError: null,
      settingsDrift: null,
    });
    rows.push(...designAppRows(resource, doc, types, diagnostics, describeTrigger));
  }
  return { apps, rows };
}

function serverRow(app: AppVariables, row: ResourceSetting, metadata: SecretMetadata | null): VariableRow {
  const property = row.origin === 'property' ? row.name : null;
  return {
    id: `${app.resourceId}/${row.name}`,
    resourceId: app.resourceId,
    resourceType: app.type,
    name: row.name,
    displayName: property ? (row.delivery.envName ?? row.name) : row.name,
    property,
    origin: row.origin,
    source: row.source,
    location: row.location,
    delivery: row.delivery,
    kind: row.kind,
    secret: row.location.secret,
    secretName: row.secretName,
    expression: row.expression,
    value: row.value,
    resolvedValue: row.resolvedValue,
    sync: row.sync,
    design: row.design,
    referenceStatus: row.referenceStatus,
    drift: row.drift,
    pickupPendingUntil: row.pickupPendingUntil,
    cloudDrift: row.cloudDrift ?? null,
    externalTrigger: row.externalTrigger ?? null,
    olderVersionInUse: row.olderVersionInUse ?? false,
    metadata,
    scope: 'deployment',
    unsaved: false,
    editable: false,
    connectionKey: null,
    problems: [],
    placeholder: false,
    request: row.request ?? null,
  };
}

/** The Variable cell's accessible name when it differs from the visible name (external trigger rows, feature spec 16 9.7). */
export function variableCellName(row: VariableRow): string | undefined {
  return row.externalTrigger
    ? strings.externalTriggers.chipName(
        row.displayName,
        row.externalTrigger.name,
        externalTriggerKind(row.externalTrigger.kind).label,
      )
    : undefined;
}

/** Deployment-scope rows from the server's answer, in its order. */
export function deploymentRows(variables: DeploymentVariables): { apps: VariableApp[]; rows: VariableRow[] } {
  const metadata = new Map(variables.secrets.map((m) => [m.name.toLowerCase(), m]));
  const apps: VariableApp[] = [];
  const rows: VariableRow[] = [];
  for (const app of variables.apps) {
    apps.push({
      resourceId: app.resourceId,
      type: app.type,
      applied: app.applied,
      referenceStatusCheckedAt: app.referenceStatusCheckedAt,
      referenceStatusError: app.referenceStatusError,
      settingsDrift: app.settingsDrift ?? null,
      platform: app.platform,
      appReferenceStatus: app.appReferenceStatus ?? null,
      lastRestart: app.lastRestart ?? null,
    });
    for (const row of app.settings) {
      rows.push(
        serverRow(app, row, row.secretName ? (metadata.get(row.secretName.toLowerCase()) ?? null) : null),
      );
    }
  }
  return { apps, rows };
}

/** What makes two design rows equal: their source and raw value. */
function designKey(row: VariableRow): string {
  return `${row.location.type}|${row.expression ?? row.value ?? ''}|${row.secretName ?? ''}|${row.location.name ?? ''}`;
}

/**
 * The designer with an overlay (spec 6.10.2): deployment rows get "Unsaved" where the open document differs
 * from its saved version, and variables only in the open document are added as design rows. Rows that are
 * entries of the open document's injection map become editable.
 */
export function withOpenDocument(
  deployment: { apps: VariableApp[]; rows: VariableRow[] },
  current: { apps: VariableApp[]; rows: VariableRow[] },
  saved: { rows: VariableRow[] } | null,
): { apps: VariableApp[]; rows: VariableRow[] } {
  const currentById = new Map(current.rows.map((r) => [r.id, r]));
  const savedById = new Map((saved?.rows ?? current.rows).map((r) => [r.id, r]));
  const differs = (id: string) => {
    const now = currentById.get(id);
    const before = savedById.get(id);
    if (!now && !before) return false;
    if (!now || !before) return true;
    return designKey(now) !== designKey(before);
  };
  const rows: VariableRow[] = deployment.rows.map((row) => {
    const open = currentById.get(row.id);
    return {
      ...row,
      unsaved: differs(row.id),
      editable: !!open?.editable,
      problems: open?.problems ?? [],
      connectionKey: open?.connectionKey ?? row.connectionKey,
    };
  });
  const known = new Set(rows.map((r) => r.id));
  for (const row of current.rows) {
    // Design trigger rows never mix into the server's rows (feature spec 16, 9.7).
    if (known.has(row.id) || row.placeholder || row.origin === 'trigger') continue;
    rows.push({ ...row, unsaved: differs(row.id) });
  }
  const apps = [...deployment.apps];
  for (const app of current.apps) if (!apps.some((a) => a.resourceId === app.resourceId)) apps.push(app);
  // Keep each app's rows together, in the apps' order.
  const order = new Map(apps.map((a, i) => [a.resourceId, i]));
  const sorted = rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => order.get(a.row.resourceId)! - order.get(b.row.resourceId)! || a.i - b.i)
    .map(({ row }) => row);
  return { apps, rows: sorted };
}

// ---------------------------------------------------------------- statuses (spec 6.10.7)

export type StatusId =
  | 'no-value'
  | 'disabled'
  | 'deleted-outside'
  | 'access-denied'
  | 'not-found'
  | 'invalid'
  | 'vault-unreachable'
  | 'changed-outside'
  | 'cloud-changed'
  | 'expired'
  | 'tags-edited'
  | 'plain-in-cloud'
  | 'unreachable'
  | 'changed'
  | 'added'
  | 'removed'
  | 'not-applied'
  | 'unsaved'
  | 'stale-value'
  | 'pending'
  | 'resolved'
  | 'applied'
  | 'read-only'
  | 'unknown'
  | 'name-conflict'
  | 'rotation-conflict'
  | 'rotated'
  | 'older-version'
  | 'rolling-out'
  | 'request-not-attached'
  | 'request-unresolved'
  | 'request-ambiguous'
  | 'request-blocked';

/** Worst first. */
export const STATUS_ORDER: readonly StatusId[] = [
  'request-blocked',
  'request-not-attached',
  'request-unresolved',
  'request-ambiguous',
  'no-value',
  'disabled',
  'deleted-outside',
  'access-denied',
  'not-found',
  'invalid',
  'vault-unreachable',
  'changed-outside',
  'name-conflict',
  'rotation-conflict',
  'cloud-changed',
  'expired',
  'tags-edited',
  'plain-in-cloud',
  'unreachable',
  'changed',
  'added',
  'removed',
  'not-applied',
  'unsaved',
  'rolling-out',
  'older-version',
  'stale-value',
  'rotated',
  'pending',
  'resolved',
  'applied',
  'read-only',
  'unknown',
];

export const STATUS_TONE: Record<StatusId, Tone> = {
  'no-value': 'danger',
  disabled: 'danger',
  'deleted-outside': 'danger',
  'access-denied': 'danger',
  'not-found': 'danger',
  invalid: 'danger',
  'vault-unreachable': 'danger',
  'changed-outside': 'warning',
  'cloud-changed': 'warning',
  expired: 'warning',
  'tags-edited': 'warning',
  'plain-in-cloud': 'warning',
  unreachable: 'warning',
  changed: 'info',
  added: 'info',
  removed: 'info',
  'not-applied': 'info',
  unsaved: 'info',
  'stale-value': 'info',
  pending: 'info',
  resolved: 'success',
  applied: 'success',
  'read-only': 'neutral',
  unknown: 'neutral',
  'name-conflict': 'warning',
  'rotation-conflict': 'warning',
  rotated: 'info',
  'older-version': 'info',
  'rolling-out': 'info',
  'request-not-attached': 'warning',
  'request-unresolved': 'warning',
  'request-ambiguous': 'warning',
  'request-blocked': 'warning',
};

export interface RowStatus {
  id: StatusId;
  tone: Tone;
  label: string;
  help: string;
}

export interface StatusContext {
  /** The vault listing failed (the response's `listingError`). */
  listingFailed: boolean;
  /** When the app's reference status was read. */
  referenceStatusCheckedAt?: string | null;
  /** When the refresh that found the app's settings drift finished. */
  settingsDriftCheckedAt?: string | null;
  /** When the last successful apply completed (grants can take 10 minutes after it). */
  lastApplyAt?: string | null;
  /** Feature spec 15: the apps that are rolling out (per-app status pending after a rollout through Nebisys). */
  rollingOut?: ReadonlySet<string>;
  /** Feature spec 15: when each per-app app read its secrets (`appReferenceStatus.readAt`). */
  appReadAt?: ReadonlyMap<string, string | null>;
  now?: number;
}

/** Role assignments can take up to 10 minutes after an apply; the hint shows for 15. */
const GRANTS_WINDOW_MS = 15 * 60_000;

const DRIFT_STATUS: Partial<Record<SecretDriftKind, StatusId>> = {
  'missing-value': 'no-value',
  disabled: 'disabled',
  'deleted-outside': 'deleted-outside',
  'changed-outside': 'changed-outside',
  expired: 'expired',
  'tags-edited': 'tags-edited',
  rotated: 'rotated',
  'rotation-conflict': 'rotation-conflict',
};

/** Store copies and generated values are plain settings in the cloud. */
export function isPlainCopy(row: Pick<VariableRow, 'secret' | 'delivery' | 'kind' | 'location'>): boolean {
  return (
    row.secret &&
    row.delivery?.mode === 'plain' &&
    (row.kind === 'generated' || row.location.capabilities.setValue === 'outside')
  );
}

export function rowStatuses(row: VariableRow, context: StatusContext): RowStatus[] {
  const ids = new Set<StatusId>();
  const app = row.resourceId;
  const now = context.now ?? Date.now();
  if (row.scope === 'deployment') {
    for (const drift of row.drift) {
      const id = DRIFT_STATUS[drift];
      if (id) ids.add(id);
    }
    const neutral = row.referenceStatus?.neutral;
    if (neutral && neutral !== 'resolved' && neutral !== 'pending') ids.add(neutral);
    if (neutral === 'resolved') ids.add('resolved');
    if (neutral === 'pending') ids.add('pending');
    if (row.secretName && row.metadata?.hasValue === null && context.listingFailed)
      ids.add('vault-unreachable');
    if (isPlainCopy(row)) ids.add('plain-in-cloud');
    if (row.cloudDrift) ids.add('cloud-changed');
    if (row.sync && row.sync !== 'in-sync') ids.add(row.sync);
    if (row.pickupPendingUntil && Date.parse(row.pickupPendingUntil) > now) ids.add('stale-value');
    if (row.metadata?.nameConflict && row.metadata.kind === 'declared') ids.add('name-conflict');
    if (row.olderVersionInUse) ids.add(context.rollingOut?.has(app) ? 'rolling-out' : 'older-version');
    if (row.sync === 'in-sync' && !row.referenceStatus && !row.request?.issue)
      ids.add(row.origin === 'platform' ? 'read-only' : 'applied');
    const issue = row.request?.issue ? REQUEST_ISSUE_STATUS[row.request.issue] : undefined;
    if (issue) ids.add(issue);
  }
  if (row.unsaved) ids.add('unsaved');
  return STATUS_ORDER.filter((id) => ids.has(id)).map((id) => ({
    id,
    // Rotated system secrets: the next apply overwrites the rotated value (feature spec 15).
    tone: id === 'rotated' && row.kind === 'system' ? 'danger' : STATUS_TONE[id],
    label: statusLabel(id, row),
    help: statusHelp(id, row, app, context, now),
  }));
}

/** The Variables rows' request issues (spec 04 6.10; feature spec 17, phase 2). */
const REQUEST_ISSUE_STATUS: Record<string, StatusId> = {
  NETWORK_NOT_ATTACHED: 'request-not-attached',
  NETWORK_REQUEST_TARGET_NOT_ATTACHED: 'request-unresolved',
  NETWORK_REQUEST_TARGET_AMBIGUOUS: 'request-ambiguous',
  NETWORK_REQUEST_PENDING: 'request-blocked',
};

/** A request row's unset value in words (6.15.24); null when the row has a value or isn't a request. */
export function requestValueText(
  row: Pick<VariableRow, 'request' | 'scope'>,
  deployment = '',
): string | null {
  const request = row.request;
  if (!request) return null;
  const rq = strings.networks.requests;
  if (row.scope === 'design') return rq.perDeployment;
  switch (request.issue) {
    case 'NETWORK_NOT_ATTACHED':
      return rq.notAttached(deployment || strings.networks.runLabels.aDeployment);
    case 'NETWORK_REQUEST_TARGET_NOT_ATTACHED':
      return request.state ? `${strings.networks.requests.notSetPrefix}${request.state}` : rq.ambiguous;
    case 'NETWORK_REQUEST_TARGET_AMBIGUOUS':
      return rq.ambiguous;
    case 'NETWORK_REQUEST_PENDING':
      return rq.blocked(request.networkName ?? strings.networks.nav.kind);
    default:
      return null;
  }
}

function statusLabel(id: StatusId, row: VariableRow): string {
  if (id === 'applied' && isPlainCopy(row)) return s.status.copied;
  if (id === 'older-version' && row.delivery)
    return deliveryEntry(row.delivery).olderVersion?.label ?? s.status[id];
  if (id === 'rotated')
    return row.kind === 'system' ? strings.vaults.aws.rotatedSystem : strings.vaults.aws.rotated;
  if (id === 'expired' && row.metadata?.expiresAt && row.location.type === 'gcp-secret-manager')
    return strings.vaults.gcp.expiredLabel(formatDay(row.metadata.expiresAt));
  if (id === 'stale-value' && row.pickupPendingUntil)
    return `${s.status['stale-value']} · ${formatClock(row.pickupPendingUntil)}`;
  return s.status[id];
}

function statusHelp(
  id: StatusId,
  row: VariableRow,
  app: string,
  context: StatusContext,
  now: number,
): string {
  const h = s.status.help;
  const restarts = row.delivery?.settingsChangeRestarts ? ` ${h.restarts(app)}` : '';
  switch (id) {
    case 'no-value':
      return h['no-value'];
    case 'disabled':
      return h.disabled(app);
    case 'deleted-outside':
      return h['deleted-outside'];
    case 'access-denied':
    case 'not-found':
    case 'invalid': {
      const recent =
        !!context.lastApplyAt &&
        now - Date.parse(context.lastApplyAt) < GRANTS_WINDOW_MS &&
        id === 'access-denied';
      return [h.reference(app), row.referenceStatus?.details, recent ? h.grantsDelay : null]
        .filter(Boolean)
        .join(' ');
    }
    case 'vault-unreachable':
      return h['vault-unreachable'];
    case 'changed-outside':
      return h['changed-outside'];
    case 'cloud-changed': {
      const ago = formatRelative(context.settingsDriftCheckedAt ?? null, now);
      if (row.cloudDrift?.change === 'removed') return h.cloudRemoved(ago);
      return h.cloudChanged(ago, row.secret ? null : (row.resolvedValue ?? row.value));
    }
    case 'expired':
      return locationEntry(row.location).driftText?.expired ?? h.expired;
    case 'tags-edited':
      return h['tags-edited'];
    case 'plain-in-cloud':
      return row.delivery ? (deliveryEntry(row.delivery).readersText(app, row.delivery) ?? '') : '';
    case 'unreachable':
      return h.unreachable;
    case 'changed':
      return h.changed + restarts;
    case 'added':
      return h.added + restarts;
    case 'removed':
      return h.removed + restarts;
    case 'not-applied':
      return h['not-applied'];
    case 'unsaved':
      return h.unsaved;
    case 'stale-value':
      return h.staleValue(app, formatClock(row.pickupPendingUntil));
    case 'pending':
      return h.pending;
    case 'resolved':
      return h.resolved(formatRelative(context.referenceStatusCheckedAt ?? null, now));
    case 'applied':
      return h.applied;
    case 'read-only':
      return h['read-only'];
    case 'unknown':
      return h.unknown;
    case 'name-conflict':
      return strings.vaults.nameConflictHelp(
        row.metadata?.nameConflict ?? '',
        locationEntry(row.location).label(row.location),
        row.secretName ?? '',
      );
    case 'rotation-conflict':
      return strings.vaults.aws.rotationConflictHelp(row.secretName ?? '');
    case 'rotated':
      return locationEntry(row.location).driftText?.rotated ?? strings.vaults.aws.rotatedHelp;
    case 'older-version': {
      const read = context.appReadAt?.get(app) ?? null;
      const entry = row.delivery ? deliveryEntry(row.delivery) : null;
      return entry?.olderVersion?.help(app, read ? formatRelative(read, now) : '') ?? '';
    }
    case 'rolling-out':
      return s.status['rolling-out'];
    case 'request-not-attached':
    case 'request-unresolved':
    case 'request-ambiguous':
    case 'request-blocked':
      return requestValueText(row) ?? '';
  }
}

/** The tone of a row's worst status, for counts. */
export function worstTone(statuses: readonly RowStatus[]): Tone | null {
  return statuses[0]?.tone ?? null;
}

/** Rows whose worst status is danger (errors) and warning (warnings), for badges (spec 6.10.1). */
export function issueCounts(statusLists: readonly (readonly RowStatus[])[]): {
  errors: number;
  warnings: number;
} {
  let errors = 0;
  let warnings = 0;
  for (const statuses of statusLists) {
    const tone = worstTone(statuses);
    if (tone === 'danger') errors++;
    else if (tone === 'warning') warnings++;
  }
  return { errors, warnings };
}

const SECRET_DRIFT_STATUS: Record<string, StatusId> = {
  'missing-value': 'no-value',
  disabled: 'disabled',
  'deleted-outside': 'deleted-outside',
  'changed-outside': 'changed-outside',
  expired: 'expired',
  'tags-edited': 'tags-edited',
};

/** The statuses of a secret in the Secrets view: its drift (spec 6.10.13), else "In sync". */
export function secretStatuses(secret: SecretMetadata, listingFailed: boolean): RowStatus[] {
  const result: RowStatus[] = [];
  for (const drift of secret.drift) {
    if (drift === 'unmanaged') {
      result.push({ id: 'unknown', tone: 'info', label: s.secretsView.unmanaged, help: '' });
      continue;
    }
    const id = SECRET_DRIFT_STATUS[drift];
    if (id) {
      const help = s.status.help[id as keyof typeof s.status.help];
      result.push({
        id,
        tone: STATUS_TONE[id],
        label: s.status[id],
        help: typeof help === 'string' ? help : '',
      });
    }
  }
  if (secret.hasValue === null && listingFailed)
    result.push({ id: 'vault-unreachable', tone: 'danger', label: s.status['vault-unreachable'], help: '' });
  if (result.length === 0 && secret.kind !== 'unmanaged')
    result.push({ id: 'applied', tone: 'success', label: s.secretsView.inSync, help: '' });
  return result;
}

// ---------------------------------------------------------------- value cells (spec 6.10.6)

export type ValueCell =
  | {
      kind: 'text';
      text: string;
      applied?: string;
      /** The cloud's value, or "removed in the cloud" (a refresh found it). */
      cloud?: string;
      struck?: boolean;
      muted?: boolean;
      label: string;
    }
  | { kind: 'set-per-deployment'; label: string }
  | {
      kind: 'secret-set';
      version: string | null;
      at: string | null;
      by: string | null;
      label: string;
      /** Reveal on: why this value can't be revealed (a disabled secret), and its explanation. */
      note?: string;
      noteHelp?: string;
    }
  | { kind: 'secret-system'; version: string | null; label: string; note?: string; noteHelp?: string }
  | {
      /** Reveal on, `capabilities.reveal`, with a value (spec 6.10.18): masked with Reveal. */
      kind: 'secret-revealable';
      /** The secret's name for the reveal request. */
      secretName: string;
      /** The name messages use: the variable in the Variables view, else the secret. */
      display: string;
      location: Pick<SettingLocation, 'type' | 'secret' | 'label' | 'container'>;
      version: string | null;
      at: string | null;
      by: string | null;
      label: string;
    }
  | { kind: 'no-value'; label: string }
  | { kind: 'store-copy'; store: string; version: number | null; at: string | null; label: string }
  | { kind: 'generated'; label: string }
  | { kind: 'unmanaged'; label: string }
  | {
      kind: 'deleted';
      deletedAt: string | null;
      recoverableUntil: string | null;
      recoverability: 'recoverable' | 'not-recoverable' | 'unknown' | null;
      label: string;
    }
  | { kind: 'unreachable'; version: string | null; at: string | null; label: string }
  | { kind: 'unknown'; label: string };

export interface ValueContext {
  /** The response's `listingError` is set. */
  listingFailed: boolean;
  /** `DeploymentVariables.appliedVersion`. */
  appliedVersion: number | null;
  /** When the last successful apply completed. */
  lastApplyAt: string | null;
  /** The installation reveals secret values (spec 6.10.6, 6.10.18): `/me` or a `capabilities.reveal` row says so. */
  revealOn?: boolean;
  /** The deployment's name (request rows' "dev isn't attached to a network"). */
  deploymentName?: string;
  now?: number;
}

/** The value cell of a secret described by its metadata (rows and the Secrets view share it). */
export function secretValueCell(
  metadata: SecretMetadata | null,
  location: Pick<SettingLocation, 'type' | 'secret' | 'container' | 'capabilities'> &
    Partial<Pick<SettingLocation, 'label'>>,
  kind: SettingKind | 'unmanaged' | null,
  context: ValueContext,
  /** The name reveal messages use (the variable's); defaults to the secret's. */
  display?: string,
): ValueCell {
  const now = context.now ?? Date.now();
  const v = s.value;
  const format = (version: string | null) =>
    version ? locationEntry(location).formatVersion(version) : null;
  if (kind === 'generated') return { kind: 'generated', label: v.label.generated };
  if (location.capabilities.setValue === 'outside') {
    const store = location.container ?? '';
    return {
      kind: 'store-copy',
      store,
      version: context.appliedVersion,
      at: context.lastApplyAt,
      label: v.label.storeCopy(store),
    };
  }
  if (kind === 'unmanaged') return { kind: 'unmanaged', label: v.label.unmanaged };
  // Secrets a connector keeps itself outside the managed vault (the AWS registry credentials): set at apply.
  if (kind === 'system' && !location.capabilities.versions)
    return { kind: 'secret-system', version: null, label: v.label.secretSystem };
  if (!metadata || metadata.hasValue === null) {
    if (context.listingFailed)
      return {
        kind: 'unreachable',
        version: format(metadata?.currentVersion ?? null),
        at: metadata?.updatedAt ?? null,
        label: v.label.unreachable,
      };
    return { kind: 'unknown', label: v.label.unknown };
  }
  if (metadata.drift.includes('deleted-outside')) {
    const when = formatRelative(metadata.deletedAt ?? null, now);
    return {
      kind: 'deleted',
      deletedAt: metadata.deletedAt ?? null,
      recoverableUntil: metadata.recoverableUntil ?? null,
      recoverability: metadata.recoverability ?? null,
      label: v.label.deleted(when),
    };
  }
  // A disabled secret still has its value (design 5.5: "Set", not "No value yet"); it just can't be read.
  const disabled = metadata.enabled === false && !!metadata.currentVersion;
  if (!metadata.hasValue && !disabled) return { kind: 'no-value', label: v.label.noValue };
  const reveal = !!context.revealOn;
  if (reveal && location.capabilities.reveal && metadata.enabled !== false)
    return {
      kind: 'secret-revealable',
      secretName: metadata.name,
      display: display ?? metadata.name,
      location: {
        type: location.type,
        secret: location.secret,
        container: location.container,
        label: location.label ?? '',
      },
      version: format(metadata.currentVersion),
      at: metadata.updatedAt,
      by: metadata.updatedBy,
      label: strings.reveal.maskedLabel(formatRelative(metadata.updatedAt, now), metadata.updatedBy),
    };
  if (kind === 'system') {
    const note = reveal && !location.capabilities.reveal;
    return {
      kind: 'secret-system',
      version: format(metadata.currentVersion),
      label: note ? `${v.label.secretSystem}, ${strings.reveal.systemNote}` : v.label.secretSystem,
      ...(note ? { note: strings.reveal.systemNote, noteHelp: strings.reveal.systemHelp } : {}),
    };
  }
  const label = v.label.secretSet(formatRelative(metadata.updatedAt, now), metadata.updatedBy);
  const cell = {
    kind: 'secret-set' as const,
    version: format(metadata.currentVersion),
    at: metadata.updatedAt,
    by: metadata.updatedBy,
  };
  if (reveal && location.capabilities.reveal && metadata.enabled === false)
    return {
      ...cell,
      label: `${label}, ${strings.reveal.disabledNote}`,
      note: strings.reveal.disabledNote,
      noteHelp: locationEntry(location).disabledRevealText,
    };
  // A declared vault secret the last applied version doesn't declare yet (spec 6.10.18): apply first.
  if (
    reveal &&
    !location.capabilities.reveal &&
    kind === 'declared' &&
    location.capabilities.setValue === 'vault'
  )
    return {
      ...cell,
      label: `${label}, ${strings.reveal.unappliedNote}`,
      note: strings.reveal.unappliedNote,
      noteHelp: strings.reveal.unappliedHelp,
    };
  return { ...cell, label };
}

export function valueCell(row: VariableRow, context: ValueContext): ValueCell {
  const v = s.value;
  // Feature spec 17, phase 2 (6.15.24): a request's settings are resolved per deployment, or say why they aren't set.
  const requestText = requestValueText(row, context.deploymentName ?? '');
  if (requestText !== null) return { kind: 'text', text: requestText, muted: true, label: requestText };
  if (row.secret) {
    if (row.scope === 'design') return { kind: 'set-per-deployment', label: v.setPerDeploymentLabel };
    if (row.sync === 'added' || row.sync === 'not-applied') {
      if (row.metadata && row.location.capabilities.setValue === 'vault' && row.metadata.hasValue === false)
        return { kind: 'no-value', label: v.label.noValue };
    }
    return secretValueCell(row.metadata, row.location, row.kind, context, row.displayName);
  }
  if (row.scope === 'design') {
    if (row.placeholder || ['connection', 'output', 'trigger'].includes(row.location.type))
      return { kind: 'text', text: v.setAtApply, muted: true, label: v.setAtApply };
    if (row.location.type === 'plain')
      return { kind: 'text', text: row.value ?? '', label: v.label.value(row.value ?? '') };
    if (row.location.type === 'parameter')
      return { kind: 'text', text: v.setPerDeploymentParam, muted: true, label: v.setPerDeploymentParam };
    return { kind: 'text', text: v.resolvedAtApply, muted: true, label: v.resolvedAtApply };
  }
  const cloud =
    row.cloudDrift?.change === 'removed'
      ? v.removedInCloud
      : row.cloudDrift?.value !== null && row.cloudDrift?.value !== undefined
        ? v.inCloud(row.cloudDrift.value)
        : undefined;
  const withCloud = <T extends ValueCell>(cell: T): T =>
    cloud && cell.kind === 'text' ? { ...cell, cloud, label: `${cell.label}, ${cloud}` } : cell;
  if (row.sync === 'changed' || row.sync === 'added') {
    const next = row.design?.resolvedValue ?? row.design?.expression ?? null;
    const text = next ?? v.resolvedAtApply;
    const applied =
      row.sync === 'changed' ? (row.resolvedValue ?? row.value ?? v.resolvedAtApply) : undefined;
    return withCloud({
      kind: 'text',
      text,
      applied,
      muted: next === null,
      label: [v.label.value(text), applied ? v.applied(applied) : ''].filter(Boolean).join(', '),
    });
  }
  const text = row.resolvedValue ?? (row.value && !row.value.includes('${') ? row.value : null);
  if (text === null)
    return withCloud({
      kind: 'text',
      text: v.resolvedAtApply,
      muted: true,
      struck: row.sync === 'removed',
      label: v.resolvedAtApply,
    });
  return withCloud({
    kind: 'text',
    text,
    struck: row.sync === 'removed',
    label: row.sync === 'removed' ? `${v.label.value(text)}, ${v.removed}` : v.label.value(text),
  });
}

/** The plain text a cell copies (Ctrl+C), or null when nothing may be copied (secret values). */
export function copyableValue(row: VariableRow, cell: ValueCell): string | null {
  if (row.secret) return null;
  return cell.kind === 'text' && !cell.muted ? cell.text : null;
}

// ---------------------------------------------------------------- summaries (node footers, inspector card)

export interface AppSummary {
  count: number;
  /** The largest group of secret rows by source label, or the number of secrets in design scope. */
  secrets: { label: string | null; count: number } | null;
  errors: number;
  warnings: number;
  /** Per-app platforms (feature spec 15): the app, for its status badge, with its delivery by reference. */
  app?: VariableApp | null;
  delivery?: SettingDelivery | null;
}

export function appSummary(rows: readonly VariableRow[], context: StatusContext): AppSummary {
  const real = rows.filter((r) => !r.placeholder && r.sync !== 'removed');
  const secretRows = real.filter((r) => r.secret);
  let secrets: AppSummary['secrets'] = null;
  if (secretRows.length) {
    if (secretRows.every((r) => r.scope === 'design')) secrets = { label: null, count: secretRows.length };
    else {
      const byLabel = new Map<string, number>();
      for (const row of secretRows) {
        const label = locationEntry(row.location).label(row.location);
        byLabel.set(label, (byLabel.get(label) ?? 0) + 1);
      }
      const [label, count] = [...byLabel].sort((a, b) => b[1] - a[1])[0];
      secrets = { label, count };
    }
  }
  const counts = issueCounts(real.map((r) => rowStatuses(r, context)));
  return { count: real.length, secrets, ...counts };
}

/** The secret name a plain variable becomes ("Create secret … and use it"): `API_KEY` → `api-key`. */
export function secretNameFor(variable: string): string {
  const name = variable
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+/, '')
    .slice(0, 63);
  return name || 'secret';
}
